export interface BMPData {
  slug: string;
  name: string;
  category: 'Erosion Control' | 'Sediment Control' | 'Runoff Control' | 'Good Housekeeping';
  description: string;
  whatToLookFor: string[];
  commonFailures: string[];
  maintenanceTips: string[];
  frequency: string;
}

export const bmpData: BMPData[] = [
  {
    slug: "silt-fence",
    name: "Silt Fence",
    category: "Sediment Control",
    description: "A temporary sediment barrier of geotextile fabric installed on contours to intercept sheet flow runoff from disturbed areas.",
    whatToLookFor: [
      "Posts should be securely driven into the ground.",
      "Fabric must be trenched in at least 6 inches deep.",
      "Fabric should be taut and attached securely to posts.",
      "Ends of the fence should wrap up-slope (J-hook) to prevent bypass."
    ],
    commonFailures: [
      "Undercutting: Water flowing underneath the fabric due to poor trenching.",
      "Overtopping: Sediment buildup exceeding 1/3 to 1/2 height of the fence.",
      "Tearing: Weathered or ripped fabric.",
      "Knockdowns: Posts falling over due to weight of sediment or wind."
    ],
    maintenanceTips: [
      "Remove sediment when it reaches 1/3 the height of the fence.",
      "Replace torn fabric immediately.",
      "Repair undercutting by re-trenching and compacting soil."
    ],
    frequency: "Weekly and within 24 hours of 0.5\" rain."
  },
  {
    slug: "stabilized-construction-entrance",
    name: "Stabilized Construction Entrance",
    category: "Good Housekeeping",
    description: "A pad of aggregate underlain with filter cloth located at any point where traffic will be entering or leaving a construction site.",
    whatToLookFor: [
      "Rock should be clean, large (3-6 inch) stone.",
      "Dimensions should meet local code (typically 50ft long x 12ft wide).",
      "Geotextile fabric should be visible underneath if required.",
      "Entrance should flare at the connection to the paved road."
    ],
    commonFailures: [
      "Track-out: Mud leaving the site on vehicle tires.",
      "Rutting: Rocks pushed into soft soil, reducing effectiveness.",
      "Sediment accumulation on the pad itself."
    ],
    maintenanceTips: [
      "Top dress with additional stone when rock becomes clogged with soil.",
      "Sweep adjacent paved roads daily if track-out occurs.",
      "Ensure all traffic uses this specific entrance."
    ],
    frequency: "Daily visual check for track-out."
  },
  {
    slug: "concrete-washout",
    name: "Concrete Washout",
    category: "Good Housekeeping",
    description: "A designated area for washing out concrete trucks, pumps, and tools to prevent highly alkaline runoff from contaminating groundwater.",
    whatToLookFor: [
      "Plastic lining must be free of tears and holes.",
      "Signage should clearly identify the area.",
      "It should be located away from storm drains (typically 50ft+).",
      "Perimeter berms should be intact."
    ],
    commonFailures: [
      "Overflowing: Washout liquids spilling over the berm.",
      "Leaking: Tears in the liner allowing liquids to seep into the ground.",
      "Improper use: Trash or other debris thrown into the washout."
    ],
    maintenanceTips: [
      "Vacuum out liquids or allow to evaporate and harden.",
      "Dispose of hardened concrete properly off-site.",
      "Replace liner immediately if damaged."
    ],
    frequency: "Before each pour and weekly."
  },
  {
    slug: "inlet-protection",
    name: "Storm Drain Inlet Protection",
    category: "Sediment Control",
    description: "A sediment filter or an impounding area around or within a storm drain inlet or curb inlet.",
    whatToLookFor: [
      "Device is firmly in place and not bypassed.",
      "Fabric or gravel bags are intact.",
      "Drainage area is not flooded excessively (causing safety hazard)."
    ],
    commonFailures: [
      "Clogging: Sediment buildup preventing any flow.",
      "Tearing: Filter bags ripping and dumping sediment into the drain.",
      "Displacement: Device pushed aside by heavy flow or traffic."
    ],
    maintenanceTips: [
      "Clean out sediment after every rain event.",
      "Replace gravel bags if they split.",
      "Remove protection once the catchment area is permanently stabilized."
    ],
    frequency: "Weekly and post-storm."
  },
  {
    slug: "check-dams",
    name: "Check Dams",
    category: "Runoff Control",
    description: "Small barriers constructed of rock, gravel bags, or sandbags across a swale or drainage ditch.",
    whatToLookFor: [
      "Center of the dam should be lower than the edges (spillway).",
      "Dams should be spaced so the toe of the upstream dam is level with the crest of the downstream dam."
    ],
    commonFailures: [
      "Bypass: Water flowing around the edges causing erosion.",
      "Sediment buildup reducing capacity.",
      "Blowout: High velocity water destroying the structure."
    ],
    maintenanceTips: [
      "Remove sediment when it reaches 1/2 the height of the dam.",
      "Restore dam height and center spillway if shifted."
    ],
    frequency: "Weekly."
  },
  {
    slug: "fiber-rolls",
    name: "Fiber Rolls (Straw Wattles)",
    category: "Sediment Control",
    description: "Tube-shaped erosion-control devices filled with straw, flax, rice, coconut fiber, or compost, wrapped in netting.",
    whatToLookFor: [
      "Installed on level contours to prevent concentrated flow.",
      "Ends turned upslope (J-hook) to contain runoff.",
      "Trenched in 2-4 inches deep to prevent undercutting.",
      "Staked securely every 4 feet."
    ],
    commonFailures: [
      "Undercutting: Runoff flowing beneath the roll due to lack of trenching.",
      "Floating: Not staked down securely.",
      "Crushed: Damaged by vehicle traffic.",
      "Splitting: Netting degraded by UV exposure."
    ],
    maintenanceTips: [
      "Remove sediment when accumulation reaches 1/2 the height of the roll.",
      "Replace split or unraveling rolls immediately.",
      "Re-stake if rolls are sagging or moved."
    ],
    frequency: "Weekly and pre/post-rain."
  },
  {
    slug: "stockpile-management",
    name: "Stockpile Management",
    category: "Good Housekeeping",
    description: "Procedures to prevent air and stormwater pollution from stockpiles of soil, paving materials, and other loose construction materials.",
    whatToLookFor: [
      "Stockpiles located away from inlets and drainage courses.",
      "Covered with plastic or tarps if inactive for >14 days.",
      "Perimeter controls (berm or silt fence) installed around the base.",
      "Plastic covers weighed down or secured against wind."
    ],
    commonFailures: [
      "Uncovered piles during rain events.",
      "Sediment washing into nearby streets or drains.",
      "Torn plastic covers.",
      "Located too close to a waterbody."
    ],
    maintenanceTips: [
      "Repair or replace torn covers.",
      "Remove sediment from perimeter controls.",
      "Water active stockpiles for dust control during dry/windy periods."
    ],
    frequency: "Weekly and pre-storm."
  },
  {
    slug: "sediment-basin",
    name: "Sediment Basin",
    category: "Sediment Control",
    description: "A temporary pond built to capture runoff from larger disturbed areas, allowing sediment to settle before discharge.",
    whatToLookFor: [
      "Outlet structure (skimmer or riser) is free of debris.",
      "Embankments are stable and not eroding.",
      "Basin is not filled with sediment (>50% capacity).",
      "Emergency spillway is stabilized with rock."
    ],
    commonFailures: [
      "Short-circuiting: Inlet too close to outlet, preventing settling.",
      "Clogged outlet causing excessive flooding.",
      "Turbid discharge leaving the site.",
      "Bank erosion."
    ],
    maintenanceTips: [
      "Remove sediment when basin is 50% full.",
      "Clean trash racks and skimmers after every storm.",
      "Repair erosion on banks and stabilize immediately."
    ],
    frequency: "Weekly and within 24 hours of rain."
  },
  {
    slug: "hydroseeding",
    name: "Hydroseeding",
    category: "Erosion Control",
    description: "A spray-on mixture of seed, mulch, fertilizer, and tackifier used to establish vegetation on disturbed soil.",
    whatToLookFor: [
      "Uniform coverage with no large bare spots (shadows).",
      "Applied to properly roughened/prepared soil.",
      "Appropriate seed mix used for the season/climate.",
      "No overspray on impervious surfaces (roads, walks)."
    ],
    commonFailures: [
      "Rilling/gullies forming underneath the mulch layer.",
      "Washed away by heavy rain before curing (requires 24h dry time usually).",
      "Applied to compacted soil where seed cannot root."
    ],
    maintenanceTips: [
      "Re-apply to bare spots or areas of washout.",
      "Ensure adequate moisture (irrigation) is provided for germination.",
      "Keep traffic off seeded areas."
    ],
    frequency: "Weekly until vegetation is established (70% density)."
  },
  {
    slug: "solid-waste-management",
    name: "Solid Waste Management",
    category: "Good Housekeeping",
    description: "Management of general construction trash, debris, and hazardous materials to prevent pollution.",
    whatToLookFor: [
      "Dumpsters have lids and are closed when not actively in use.",
      "No loose trash scattered around the site.",
      "Hazardous waste (paint, solvents) separated from general trash.",
      "Dumpsters are not leaking."
    ],
    commonFailures: [
      "Dumpsters overflowing.",
      "Lids left open during rain events (creating 'dumpster juice').",
      "Hazardous materials disposed of in general trash.",
      "Windblown debris."
    ],
    maintenanceTips: [
      "Schedule more frequent pickups if dumpsters are consistently full.",
      "Replace leaking dumpsters immediately.",
      "Conduct daily site walks to pick up loose litter."
    ],
    frequency: "Daily visual check, Weekly formal inspection."
  },
  {
    slug: "erosion-control-blankets",
    name: "Erosion Control Blankets (ECB)",
    category: "Erosion Control",
    description: "Protective blanket of straw, jute, or coconut fiber installed on slopes to prevent soil loss and aid vegetation establishment.",
    whatToLookFor: [
      "Installed vertically down the slope (never across).",
      "Overlaps should be shingled (upstream over downstream).",
      "Staples/stakes used according to manufacturer pattern.",
      "Good contact with the soil (no tenting)."
    ],
    commonFailures: [
      "Tenting: Gaps between blanket and soil allowing water to flow underneath.",
      "Insufficient stapling causing blanket to blow away or slip.",
      "Improper overlap allowing rills to form at seams."
    ],
    maintenanceTips: [
      "Repair or replace sections that have slipped or torn.",
      "Add additional staples to loose areas.",
      "Monitor for vegetation growth through the mat."
    ],
    frequency: "Weekly."
  },
  {
    slug: "dust-control",
    name: "Dust Control",
    category: "Erosion Control",
    description: "Practices to reduce windborne sediment, including water application, tackifiers, and wind barriers.",
    whatToLookFor: [
      "Visible moisture on haul roads during dry days.",
      "No visible dust plumes leaving the site boundary.",
      "Reduced vehicle speeds on site."
    ],
    commonFailures: [
      "Roads drying out on weekends.",
      "Excessive speed by delivery trucks.",
      "Applying too much water, causing runoff and track-out."
    ],
    maintenanceTips: [
      "Re-apply water or tackifier as needed based on weather.",
      "Install wind fences for long-term inactive piles."
    ],
    frequency: "Daily during dry/windy weather."
  },
  {
    slug: "dewatering-operations",
    name: "Dewatering Operations",
    category: "Runoff Control",
    description: "Removing accumulated water from trenches or excavations without discharging sediment-laden water.",
    whatToLookFor: [
      "Discharge must pass through a sediment filter (dirt bag) or basin.",
      "Discharge should be to a vegetated area, not directly to a storm drain.",
      "Water should run clear from the filter device."
    ],
    commonFailures: [
      "Bypassing filters to pump faster.",
      "Filter bags bursting from over-pressure or sediment load.",
      "Discharging directly into the street or gutter."
    ],
    maintenanceTips: [
      "Replace filter bags when they are half full of sediment.",
      "Monitor pumps continuously while running.",
      "Stabilize the discharge point to prevent scouring."
    ],
    frequency: "Continuous monitoring during operation."
  }
];
